import React, { useCallback, useEffect, useMemo, useState } from "react";

import { Box } from "@mui/material";
import {
  AutocompleteMultiSelect,
  SelectOption
} from "components/reports/AutocompleteMultiSelect/AutocompleteMultiSelect";
import { useMeasures } from "hooks";
import { useAccountSelector, useDealActions, useMeasuresSelector, useReportsMetaSelector } from "store";
import type { CardConfig, ChartCardConfig, ChartType } from "store/types";
import { normalizeGroupByCharacteristic } from "utils";

const CHART_TYPE_OPTIONS: SelectOption<string>[] = [
  { key: "pie", value: "pie", text: "Pie" },
  { key: "bar", value: "bar", text: "Bar" },
  { key: "column", value: "column", text: "Column" },
  { key: "barStacked", value: "barStacked", text: "Stacked Bar" },
  { key: "columnStacked", value: "columnStacked", text: "Stacked Column" },
  { key: "waterfall", value: "waterfall", text: "Waterfall" },
  { key: "top5", value: "top5", text: "Top 5" },
  { key: "scatter", value: "scatter", text: "X/Y Scatter" },
  { key: "clusterBar", value: "clusterBar", text: "Cluster Bar" }
];

interface Props {
  config: ChartCardConfig;
  onUpdateConfig: (c: CardConfig) => void;
}

export const ChartSettings: React.FC<Props> = ({ config, onUpdateConfig }) => {
  const { getMeasuresAction } = useDealActions();
  const measures = useMeasuresSelector();
  const { characteristics } = useReportsMetaSelector();
  const { support_investor_groups } = useAccountSelector() || {};

  const [loadingMeasures, setLoadingMeasures] = useState(false);

  useEffect(() => {
    if (measures.length === 0) {
      setLoadingMeasures(true);
      getMeasuresAction().finally(() => setLoadingMeasures(false));
    }
  }, [measures.length, getMeasuresAction]);

  const measureOptions: SelectOption<string>[] = useMemo(
    () => measures.map((m) => ({ key: m.name, value: m.name, text: m.name })),
    [measures]
  );

  const groupedValues = useMeasures(false);
  const axisOptions: SelectOption<string>[] = useMemo(() => {
    return groupedValues
      .filter((v) => typeof v === "string" || (typeof v === "object" && (v as any).role !== "category"))
      .map((v) => {
        if (typeof v === "string") return { key: v, value: v, text: v };
        const val = (v as any).value || (v as any).text;
        const text = (v as any).text || (v as any).value;
        return { key: val, value: val, text };
      });
  }, [groupedValues]);

  const toAxis = (value: string) => {
    const isCalc = value.startsWith("calc-field-");
    return { type: isCalc ? "calc" : "measure", name: isCalc ? value.replace("calc-field-", "") : value };
  };

  const groupByOptions: SelectOption<string>[] = useMemo(
    () => [
      { key: "investor", value: "investor", text: "Investor" },
      ...(support_investor_groups ? [{ key: "investorGroup", value: "investorGroup", text: "Investor Group" }] : []),
      { key: "deal", value: "deal", text: "Deal" },
      ...characteristics.map((char) => {
        const val = normalizeGroupByCharacteristic(char);
        return { key: val, value: val, text: char };
      })
    ],
    [characteristics, support_investor_groups]
  );

  const currentChart = useMemo(() => {
    if (!config.graphDataFormation)
      return {
        chartType: config.chartType,
        measure: "",
        groupBy: "",
        xAxis: undefined as { name: string; type: string } | undefined,
        yAxis: undefined as { name: string; type: string } | undefined
      };
    const key = Object.keys(config.graphDataFormation)[0];
    const entry = config.graphDataFormation[key];
    return {
      chartType: entry?.chartType || config.chartType,
      measure: entry?.measure || "",
      groupBy: entry?.groupBy || "",
      xAxis: entry?.xAxis,
      yAxis: entry?.yAxis
    };
  }, [config]);

  const updateChart = useCallback(
    (updates: {
      chartType?: ChartType;
      measure?: string;
      groupBy?: string;
      xAxis?: { name: string; type: string };
      yAxis?: { name: string; type: string };
    }) => {
      const next = { ...currentChart, ...updates };
      const graphDataFormation = {
        chart1: {
          measure: next.measure || "",
          groupBy: next.groupBy || "",
          chartType: next.chartType,
          ...(next.chartType === "scatter" && next.xAxis && { xAxis: next.xAxis }),
          ...(next.chartType === "scatter" && next.yAxis && { yAxis: next.yAxis })
        }
      };
      onUpdateConfig({ ...config, chartType: next.chartType, graphDataFormation });
    },
    [config, currentChart, onUpdateConfig]
  );

  return (
    <>
      <Box>
        <AutocompleteMultiSelect
          key={currentChart.chartType}
          defaultValue={CHART_TYPE_OPTIONS.find((o) => o.value === currentChart.chartType)}
          options={CHART_TYPE_OPTIONS}
          label="Chart Type"
          selectAll={false}
          disabledClean
          onChange={(value) => updateChart({ chartType: value as ChartType })}
        />
      </Box>
      {currentChart.chartType === "scatter" ? (
        <>
          <Box>
            <AutocompleteMultiSelect
              key={`x-${currentChart.xAxis?.name}`}
              defaultValue={axisOptions.find((o) => o.value === currentChart.xAxis?.name)}
              options={axisOptions}
              label="X Axis"
              selectAll={false}
              loading={loadingMeasures}
              onChange={(value) => updateChart({ xAxis: value ? toAxis(value as string) : undefined })}
            />
          </Box>
          <Box>
            <AutocompleteMultiSelect
              key={`y-${currentChart.yAxis?.name}`}
              defaultValue={axisOptions.find((o) => o.value === currentChart.yAxis?.name)}
              options={axisOptions}
              label="Y Axis"
              selectAll={false}
              loading={loadingMeasures}
              onChange={(value) => updateChart({ yAxis: value ? toAxis(value as string) : undefined })}
            />
          </Box>
          <Box>
            <AutocompleteMultiSelect
              key={`groupBy-scatter-${currentChart.groupBy}`}
              defaultValue={groupByOptions.find((o) => o.value === currentChart.groupBy)}
              options={groupByOptions}
              label="Group By"
              selectAll={false}
              onChange={(value) => updateChart({ groupBy: (value as string) || "" })}
            />
          </Box>
        </>
      ) : (
        <>
          <Box>
            <AutocompleteMultiSelect
              key={currentChart.measure}
              defaultValue={measureOptions.find((o) => o.value === currentChart.measure)}
              options={measureOptions}
              label="Value"
              selectAll={false}
              loading={loadingMeasures}
              onChange={(value) => updateChart({ measure: (value as string) || "" })}
            />
          </Box>
          <Box>
            <AutocompleteMultiSelect
              key={`groupBy-${currentChart.groupBy}`}
              defaultValue={groupByOptions.find((o) => o.value === currentChart.groupBy)}
              options={groupByOptions}
              label="Group By"
              selectAll={false}
              onChange={(value) => updateChart({ groupBy: (value as string) || "" })}
            />
          </Box>
        </>
      )}
    </>
  );
};
